import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, orderBy, limit, updateDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { Bell } from 'lucide-react';
import type { Notification } from '@allemnii-pulse/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { useCollection, useDocument } from '../../lib/firestore.js';
import { db } from '../../lib/firebase.js';
import { cn } from '../../lib/utils.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/Popover.js';
import { Badge } from '../../components/ui/Badge.js';
import { NOTIFICATION_CATEGORY_LABEL, projectIdFromLink } from './notificationMeta.js';
import { relativeTime } from '../../lib/time.js';

const CONSTRAINTS = [orderBy('createdAt', 'desc'), limit(50)];

/** One group's heading — "Other" for notifications whose link isn't a project route, otherwise
 * the project's name (live, so a rename is reflected without touching the notification). */
function GroupHeading({ projectId }: { projectId: string | null }) {
  const { data: project } = useDocument<{ name: string }>(
    projectId ? `projects/${projectId}` : null,
  );
  return (
    <div className="bg-surface-2 px-3 py-1 text-[10px] font-medium uppercase text-muted">
      {projectId ? (project?.name ?? 'Project') : 'Other'}
    </div>
  );
}

/**
 * In-app notification bell — unread count, a live panel grouped by project, mark-read on
 * select and mark-all-read. See docs/PLAN.md §7.3. Push (FCM) is the separate opt-in flow in
 * Settings (lib/messaging.ts); this always works since it's just Firestore reads.
 */
export function NotificationBell({ className }: { className?: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: notifications, loading } = useCollection<Notification>(
    user ? `users/${user.uid}/notifications` : null,
    CONSTRAINTS,
  );
  const unreadCount = notifications.filter((n) => !n.readAt).length;

  const groups = useMemo(() => {
    const map = new Map<string | null, Notification[]>();
    for (const n of notifications) {
      const projectId = projectIdFromLink(n.link);
      if (!map.has(projectId)) map.set(projectId, []);
      map.get(projectId)!.push(n);
    }
    return Array.from(map.entries());
  }, [notifications]);

  async function markRead(n: Notification) {
    if (!user || n.readAt) return;
    await updateDoc(doc(db, `users/${user.uid}/notifications/${n.id}`), {
      readAt: serverTimestamp(),
    });
  }

  async function markAllRead() {
    if (!user) return;
    const unread = notifications.filter((n) => !n.readAt);
    if (unread.length === 0) return;
    const batch = writeBatch(db);
    for (const n of unread) {
      batch.update(doc(db, `users/${user.uid}/notifications/${n.id}`), {
        readAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }

  function handleSelect(n: Notification) {
    void markRead(n);
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          'relative inline-flex h-10 w-10 items-center justify-center rounded-lg md:h-8 md:w-8 outline-none transition-colors hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-focus',
          className,
        )}
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="absolute -right-1 -top-1 h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </PopoverTrigger>
      {/* Capped to the screen on a phone (the bell sits at the top bar's right edge there), and
          the list to the space Radix reports below the bell, so a short or landscape screen
          scrolls the list instead of cutting off its end. */}
      <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)] p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-xs font-semibold">Notifications</span>
          {unreadCount > 0 && (
            <button
              type="button"
              className="-my-1.5 py-1.5 text-xs text-accent hover:underline"
              onClick={() => void markAllRead()}
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[min(24rem,calc(var(--radix-popover-content-available-height)-2.5rem))] overflow-auto overscroll-contain">
          {loading ? (
            <p className="p-4 text-sm text-muted">Loading…</p>
          ) : notifications.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted">No notifications yet.</p>
          ) : (
            groups.map(([projectId, items]) => (
              <Fragment key={projectId ?? '__other__'}>
                <GroupHeading projectId={projectId} />
                {items.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleSelect(n)}
                    className="flex w-full flex-col gap-0.5 border-b border-border px-3 py-2 text-left text-sm hover:bg-surface-2"
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      {!n.readAt && (
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                      )}
                      <span
                        className={cn(
                          'min-w-0 font-medium wrap-break-word',
                          n.readAt && 'text-muted',
                        )}
                      >
                        {n.title}
                      </span>
                    </span>
                    {n.body && <span className="text-xs text-muted wrap-break-word">{n.body}</span>}
                    <span className="text-[10px] text-muted">
                      {NOTIFICATION_CATEGORY_LABEL[n.category]} · {relativeTime(n.createdAt)}
                    </span>
                  </button>
                ))}
              </Fragment>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
